import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import {
  useGetApiSelflearnGetAssetReportOptionsAssetId,
  useGetApiSelflearnGetAssetTypes,
  usePutApiSelflearnPostAssetFeedBackReport,
} from "@oneapp/data";
import { Box, Button, Icon, Modal, Text, TextArea } from "@repo/ui";
import { useTheme } from "@repo/tokens";
import { useIsMobile } from "@repo/utils";
import styles from "./ExerciseReportModalWeb.module.scss";

type ReportOptionItem = {
  reportId: number;
  reportOption: string;
  isActive?: boolean;
};

type AssetTypeItem = {
  assetTypeId?: number;
  assetName?: string;
  active?: boolean;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  assetName?: string;
  examId?: number;
  gradeId?: number;
  feedBack?: boolean;
};

export function ExerciseReportModalWeb({
  isOpen,
  onClose,
  assetName,
  examId,
  gradeId,
  feedBack = false,
}: Props) {
  const { theme } = useTheme();
  const { t } = useTranslation("common");
  const { t: ts } = useTranslation("selflearn");
  const [selectedReportIds, setSelectedReportIds] = useState<number[]>([]);
  const [otherIssue, setOtherIssue] = useState("");

  /** Bottom sheet only on phone; tablet and desktop use a centered dialog. */
  const isBottomSheet = useIsMobile(767);
  /** Web desktop (≥1025): checkbox border token and tighter modal radius. */
  const isDesktopWeb = !useIsMobile(1025);

  const { data: assetTypes } = useGetApiSelflearnGetAssetTypes({
    request: {
      clientName: "selflearn",
    },
  });

  const assetTypeId = useMemo(() => {
    if (!Array.isArray(assetTypes) || !assetName) {
      return undefined;
    }
    return (assetTypes as AssetTypeItem[]).find(
      (item) => item?.active && item?.assetName === assetName,
    )?.assetTypeId;
  }, [assetName, assetTypes]);

  const { data: assetReportOptions } =
    useGetApiSelflearnGetAssetReportOptionsAssetId(Number(assetTypeId ?? 0), {
      query: {
        enabled: isOpen && Number(assetTypeId ?? 0) > 0,
      },
      request: {
        clientName: "selflearn",
      },
    });

  const { mutateAsync: submitAssetFeedbackReport } =
    usePutApiSelflearnPostAssetFeedBackReport({
      request: {
        clientName: "selflearn",
      },
    });

  const activeReportOptions = useMemo(
    () =>
      Array.isArray(assetReportOptions)
        ? (assetReportOptions as ReportOptionItem[]).filter(
            (option) => option?.isActive,
          )
        : [],
    [assetReportOptions],
  );

  const canSubmit =
    selectedReportIds.length > 0 || otherIssue.trim().length > 0;

  const checkboxUncheckedColor = isDesktopWeb
    ? theme.colors.neutral[50]
    : theme.colors.neutral[20];

  const modalChromeStyles = useMemo(() => {
    const bodyBase = {
      display: "flex" as const,
      flexDirection: "column" as const,
      minHeight: 0,
      overflow: "hidden" as const,
    };

    if (isBottomSheet) {
      return {
        overlay: {
          padding: 0,
        },
        modal: {
          minHeight: 0,
          maxHeight: "min(92dvh, 92vh)",
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          borderTopLeftRadius: "40px",
          borderTopRightRadius: "40px",
          boxShadow: "0 1px 100px rgba(0, 0, 0, 0.32)",
        },
        body: bodyBase,
      };
    }

    const corner = isDesktopWeb ? "24px" : "40px";
    return {
      overlay: {
        padding: "var(--spacing-lg)",
        boxSizing: "border-box" as const,
      },
      modal: {
        minHeight: 0,
        maxHeight: "min(90dvh, 90vh)",
        borderTopLeftRadius: corner,
        borderTopRightRadius: corner,
        borderBottomLeftRadius: corner,
        borderBottomRightRadius: corner,
        boxShadow: "0 1px 100px rgba(0, 0, 0, 0.32)",
      },
      body: bodyBase,
    };
  }, [isBottomSheet, isDesktopWeb]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedReportIds([]);
      setOtherIssue("");
    }
  }, [isOpen]);

  const toggleReportOption = (reportId: number) => {
    setSelectedReportIds((previous) =>
      previous.includes(reportId)
        ? previous.filter((id) => id !== reportId)
        : [...previous, reportId],
    );
  };

  const handleSubmit = async () => {
    if (!assetTypeId || !assetName || !examId || !gradeId || !canSubmit) {
      return;
    }
    try {
      await submitAssetFeedbackReport({
        data: {
          assetTypeId,
          userReportOptionIds: selectedReportIds,
          assetName,
          examId,
          gradeId,
          feedBack,
          otherIssue: otherIssue.trim() || undefined,
        },
      });
      onClose();
    } catch (error) {
      console.error(
        "[ExerciseReportModalWeb] failed to submit asset feedback report",
        error,
      );
    }
  };

  const cssVars = {
    "--rq-title-color": theme.colors.neutral[20],
    "--rq-title-mweb": theme.colors.neutral[10],
    "--rq-option-web": theme.colors.neutral[10],
    "--rq-option-mweb": theme.colors.neutral[20],
    "--rq-primary-40": theme.colors.primary[40],
  } as CSSProperties;

  const modalWidth = isBottomSheet ? "100%" : "min(calc(100% - 32px), 640px)";

  return (
    <Modal
      isModalOpen={isOpen}
      onClose={onClose}
      showHeader={false}
      showFooter={false}
      justifyContent={isBottomSheet ? "flex-end" : "center"}
      alignItems={isBottomSheet ? "flex-start" : "center"}
      width={modalWidth}
      height="auto"
      modalClassName={isBottomSheet ? styles.modalSheet : styles.modalCenter}
      styles={modalChromeStyles}
      body={
        <div
          className={
            isBottomSheet
              ? `${styles.box} ${styles.boxSheet}`
              : `${styles.box} ${styles.boxCentered}`
          }
          style={cssVars}
        >
          <div className={styles.headerRow}>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={onClose}
              aria-label={t("reportModal.cancel")}
            >
              <Icon
                name="closeIcon"
                width={24}
                height={24}
                color={theme.colors.primary[40]}
              />
            </button>
          </div>
          <div className={styles.scroll}>
            <Text className={styles.title}>{t("reportModal.title")}</Text>
            <div className={styles.optionList}>
              {activeReportOptions.map((option) => (
                <button
                  key={option.reportId}
                  type="button"
                  className={styles.optionRow}
                  onClick={() => toggleReportOption(option.reportId)}
                >
                  <Box className={styles.checkboxWrap}>
                    <Icon
                      name={
                        selectedReportIds.includes(option.reportId)
                          ? "checkboxChecked"
                          : "checkbox"
                      }
                      size={18}
                      color={
                        selectedReportIds.includes(option.reportId)
                          ? theme.colors.primary[40]
                          : checkboxUncheckedColor
                      }
                    />
                  </Box>
                  <span className={styles.optionLabel}>
                    {option.reportOption}
                  </span>
                </button>
              ))}
            </div>
            <div className={styles.textAreaWrap}>
              <TextArea
                value={otherIssue}
                onChangeText={setOtherIssue}
                variant="floating"
                label={ts("reviseQuestions.report.otherIssueLabel")}
                placeholder={ts("reviseQuestions.report.otherIssuePlaceholder")}
                borderRadius={theme.radii.xl}
                borderColor={theme.colors.neutral[60]}
                labelColor={theme.colors.neutral[60]}
                placeholderColor={theme.colors.neutral[60]}
                rows={1}
                resize="none"
              />
            </div>
          </div>
          <div className={styles.footer}>
            <div className={styles.submitBtnWrap}>
              <Button
                variant="primary"
                width="100%"
                height={56}
                shape="circle"
                onPress={() => void handleSubmit()}
                disabled={!canSubmit}
                backgroundColor={
                  canSubmit
                    ? theme.colors.primary[30]
                    : theme.colors.neutral[90]
                }
                textColor={
                  canSubmit
                    ? theme.colors.neutral[100]
                    : theme.colors.neutral[60]
                }
              >
                {t("reportModal.submit")}
              </Button>
            </div>
          </div>
        </div>
      }
    />
  );
}
